import React, { useState } from "react";
import Navbar from "../components/Navbar";
import MarineSparesAdvantages from "../components/MarineSparesAdvantages";
import Contact from "../components/Contact";
import Breadcrumb from "../components/Breadcrumb";
import useSEO from "../hooks/useSEO";
import { SITE } from "../constants/siteConfig";
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS, TRANSITIONS, GRADIENTS, GLASSMORPHISM, HERO_HEIGHTS, ANIMATIONS } from '../constants/designTokens';

const subItems = [
    "Optical, Ionization & Duct Smoke Detectors",
    "Addressable Heat & UV/IR Flame Detectors",
    "Manual Call Points & Audible Alarm Bells",
    "Intrinsically Safe (I.S.) Barriers & Isolators",
    "Fixed & Portable Gas Detection Sensors",
    "Test Aerosols, Gases & Spares"
];

const faqs = [
    {
        question: "Are your fire and smoke detectors compatible with both addressable and conventional alarm panels?",
        answer:
            "Yes. We supply detectors and components for both addressable loop-based systems and conventional zone-wired fire alarm panels. Our team cross-references your existing panel type, loop protocol, and detector base configuration to ensure full compatibility before shipping, avoiding costly integration issues onboard."
    },
    {
        question: "Are the detectors you supply type-approved for marine use?",
        answer:
            "All optical, ionization, duct, heat, and flame detectors we supply carry the required marine type-approval certification from recognized classification societies, ensuring compliance with SOLAS Chapter II-2 and the FSS Code. Full documentation, including certificates of conformity, is provided with every order."
    },
    {
        question: "Can you source replacements for obsolete or discontinued fire detection panels?",
        answer:
            "Absolutely. Many vessels still operate legacy Consilium, Salwico, or Autronica detection panels that are no longer in active production. Our procurement network sources original, refurbished, and fully compatible equivalent detectors, call points, and bases for these older systems, extending their service life without a full panel replacement."
    },
    {
        question: "Do you supply Intrinsically Safe (I.S.) barriers for hazardous area installations?",
        answer:
            "Yes, we source I.S. barriers and isolators certified for use in hazardous zones such as tank decks, pump rooms, and cargo areas on tankers and gas carriers. These components are matched to your specific detector loop and hazardous area classification to maintain system integrity and compliance."
    },
    {
        question: "Will the supplied parts be accepted during SOLAS/FSS Code class surveys?",
        answer:
            "All fire and smoke detection spares we supply come with the certification and documentation required to satisfy class surveyors during SOLAS and FSS Code surveys, including test certificates, conformity declarations, and, where applicable, class society approval references for Consilium, Salwico, Autronica, and Apollo equipment."
    }
];

const FireSmoke = () => {
    useSEO({
        title: 'Marine Fire & Smoke Detectors',
        description: 'SOLAS-compliant marine fire and smoke detection spares: optical, ionization & duct smoke detectors, flame detectors, call points, and I.S. barriers from Consilium, Salwico, Autronica, and Apollo. Fast global delivery.',
        canonical: '/fire-smoke',
        ogImage: 'https://marine-sealink.com/img/Logo-Full_White.png',
        breadcrumb: [
            { name: 'Home', url: '/' },
            { name: 'Fire & Smoke Detectors', url: '/fire-smoke' }
        ],
        faqs
    });

    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = idx => {
        setOpenIndex(openIndex === idx ? null : idx);
    };

    const whatsappMessage = encodeURIComponent(
        "Hello, I need assistance with marine fire & smoke detection spare parts."
    );

    return (
    <div className="page-transition">
        <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Fire & Smoke Detectors', href: '/fire-smoke' }]} />
        <style>{`
            @keyframes gradientShift {
                ${ANIMATIONS.gradientShift}
            }

            @keyframes pulseGlow {
                ${ANIMATIONS.pulseGlow}
            }

            .fire-smoke-hero {
                background: ${GRADIENTS.animatedHero};
                background-size: 400% 400%;
                animation: gradientShift 15s ease infinite;
            }

            @media (max-width: 767.98px) {
                .fire-smoke-hero > div {
                    max-width: 90% !important;
                    padding: 2rem 1.5rem !important;
                }
                .fire-smoke-hero h1 {
                    font-size: 1.75rem !important;
                }
                .fire-smoke-hero p {
                    font-size: 1rem !important;
                }
                .category-detail-card {
                    padding: 1.5rem !important;
                }
                .category-detail-card h2 {
                    font-size: 1.5rem !important;
                }
                .cta-section h2 {
                    font-size: 1.5rem !important;
                }
                .cta-buttons {
                    flex-direction: column !important;
                    gap: 1rem !important;
                }
                .cta-btn {
                    width: 100% !important;
                    justify-content: center !important;
                }
                .faq-header h5 {
                    font-size: 1rem !important;
                }
            }

            @media (max-width: 480px) {
                .fire-smoke-hero > div {
                    padding: 1.5rem 1rem !important;
                }
            }

            .category-detail-card {
                background: ${COLORS.background.white};
                border-radius: ${RADIUS.xl};
                padding: ${SPACING['2xl']};
                box-shadow: ${SHADOWS.md};
                border: 1px solid ${COLORS.ui.border};
                position: relative;
                overflow: hidden;
            }

            .category-detail-card::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 4px;
                background: linear-gradient(90deg, ${COLORS.primary.teal} 0%, ${COLORS.primary.dark} 100%);
            }

            .category-icon-lg {
                width: 80px;
                height: 80px;
                background: linear-gradient(135deg, ${COLORS.primary.teal} 0%, ${COLORS.primary.dark} 100%);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 2rem;
                color: ${COLORS.text.white};
                margin-bottom: ${SPACING.lg};
                box-shadow: 0 4px 20px rgba(64, 168, 196, 0.3);
            }

            .category-items {
                list-style: none;
                padding: 0;
                margin: ${SPACING.lg} 0;
            }

            .category-items li {
                padding: ${SPACING.sm} 0;
                color: ${COLORS.text.primary};
                font-size: ${TYPOGRAPHY.fontSize.base};
                display: flex;
                align-items: center;
                gap: ${SPACING.sm};
                border-bottom: 1px solid ${COLORS.background.light};
            }

            .category-items li:last-child {
                border-bottom: none;
            }

            .category-items li::before {
                content: '\\f06d';
                font-family: 'Font Awesome 6 Free';
                font-weight: 900;
                color: ${COLORS.primary.teal};
                flex-shrink: 0;
                font-size: ${TYPOGRAPHY.fontSize.sm};
            }

            .brands-tag {
                background: ${COLORS.background.lightTeal};
                border-radius: ${RADIUS.lg};
                padding: ${SPACING.md} ${SPACING.lg};
                font-size: ${TYPOGRAPHY.fontSize.sm};
                color: ${COLORS.primary.dark};
                margin-top: ${SPACING.lg};
                display: inline-block;
            }

            .brands-tag strong {
                color: ${COLORS.primary.teal};
            }

            .compliance-note {
                background: ${COLORS.background.lightBlue};
                border-left: 4px solid ${COLORS.primary.teal};
                border-radius: ${RADIUS.md};
                padding: ${SPACING.md} ${SPACING.lg};
                margin-top: ${SPACING.lg};
                color: ${COLORS.text.primary};
                font-size: ${TYPOGRAPHY.fontSize.sm};
                line-height: ${TYPOGRAPHY.lineHeight.relaxed};
            }

            .compliance-note strong {
                color: ${COLORS.primary.dark};
            }

            .cta-section {
                background: linear-gradient(135deg, ${COLORS.primary.dark} 0%, #1a3a4f 50%, ${COLORS.primary.teal} 100%);
                position: relative;
                overflow: hidden;
            }

            .cta-section::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
                opacity: 0.5;
            }

            .cta-btn {
                display: inline-flex;
                align-items: center;
                gap: ${SPACING.sm};
                padding: ${SPACING.md} ${SPACING.xl};
                border-radius: ${RADIUS.lg};
                font-weight: ${TYPOGRAPHY.fontWeight.semibold};
                font-size: ${TYPOGRAPHY.fontSize.base};
                text-decoration: none;
                transition: ${TRANSITIONS.all};
                border: 2px solid transparent;
            }

            .cta-btn-primary {
                background: ${COLORS.background.white};
                color: ${COLORS.primary.dark};
                box-shadow: 0 4px 20px rgba(255, 255, 255, 0.2);
            }

            .cta-btn-primary:hover {
                background: ${COLORS.primary.teal};
                color: ${COLORS.text.white};
                transform: translateY(-3px);
                box-shadow: 0 8px 30px rgba(64, 168, 196, 0.4);
            }

            .cta-btn-whatsapp {
                background: ${COLORS.ui.whatsapp};
                color: ${COLORS.text.white};
                box-shadow: 0 4px 20px rgba(37, 211, 102, 0.3);
            }

            .cta-btn-whatsapp:hover {
                background: ${COLORS.ui.whatsappHover};
                color: ${COLORS.text.white};
                transform: translateY(-3px);
                box-shadow: 0 8px 30px rgba(37, 211, 102, 0.5);
            }

            .faq-section h2 {
                color: ${COLORS.primary.dark};
                font-weight: ${TYPOGRAPHY.fontWeight.bold};
                text-align: center;
                margin-bottom: ${SPACING.md};
            }
            .faq-section .section-subtitle {
                color: ${COLORS.text.primary};
                font-size: ${TYPOGRAPHY.fontSize.base};
                text-align: center;
                max-width: 600px;
                margin: 0 auto ${SPACING['2xl']};
            }
            .faq-card {
                border-radius: ${RADIUS.lg};
                box-shadow: ${SHADOWS.sm};
                margin-bottom: ${SPACING.md};
                overflow: hidden;
                border: 1px solid ${COLORS.ui.border};
                transition: all 0.3s ease;
            }
            .faq-card:hover {
                box-shadow: ${SHADOWS.md};
            }
            .faq-header {
                background: ${COLORS.background.lightBlue};
                cursor: pointer;
                padding: ${SPACING.lg} ${SPACING.xl};
                transition: all 0.3s ease;
            }
            .faq-header:hover {
                background: ${COLORS.background.lightTeal};
            }
            .faq-header h5 {
                color: ${COLORS.primary.teal};
                font-weight: ${TYPOGRAPHY.fontWeight.semibold};
                font-size: ${TYPOGRAPHY.fontSize.md};
                margin: 0;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: ${SPACING.md};
            }
            .faq-header h5 span {
                flex-shrink: 0;
                font-weight: ${TYPOGRAPHY.fontWeight.normal};
                font-size: 1.5rem;
                line-height: 1;
                transition: transform 0.3s ease;
            }
            .faq-card.open .faq-header h5 span {
                transform: rotate(45deg);
            }
            .faq-body {
                color: ${COLORS.text.primary};
                font-size: ${TYPOGRAPHY.fontSize.base};
                background: ${COLORS.background.lightTeal};
                padding: ${SPACING.lg} ${SPACING.xl};
                line-height: ${TYPOGRAPHY.lineHeight.relaxed};
                border-top: 1px solid rgba(64, 168, 196, 0.2);
            }

            @media (max-width: 767.98px) {
                .faq-header {
                    min-height: 60px;
                    display: flex;
                    align-items: center;
                    padding: ${SPACING.md} ${SPACING.lg};
                }
                .faq-header h5 {
                    line-height: 1.4;
                }
                .faq-body {
                    font-size: 0.95rem !important;
                    padding: ${SPACING.md} ${SPACING.lg};
                }
            }

            @media (max-width: 480px) {
                .faq-header h5 {
                    font-size: 0.95rem !important;
                }
                .faq-body {
                    font-size: 0.9rem !important;
                }
            }
        `}</style>
        <Navbar />

        {/* Hero Section with animated gradient background */}
        <section
            className="fire-smoke-hero d-flex align-items-center justify-content-center"
            style={{
                minHeight: HERO_HEIGHTS.page,
                position: "relative",
                color: COLORS.text.white,
                textAlign: "center"
            }}
        >
            <div
                style={{
                    background: GLASSMORPHISM.strong.background,
                    backdropFilter: GLASSMORPHISM.strong.backdropFilter,
                    WebkitBackdropFilter: GLASSMORPHISM.strong.WebkitBackdropFilter,
                    padding: `${SPACING['2xl']} ${SPACING.xl}`,
                    borderRadius: RADIUS['2xl'],
                    boxShadow: GLASSMORPHISM.strong.boxShadow,
                    border: GLASSMORPHISM.strong.border,
                    maxWidth: '700px',
                    margin: "0 auto",
                    animation: 'fadeInUp 0.8s ease-out'
                }}
            >
                <h1 style={{
                    fontWeight: TYPOGRAPHY.fontWeight.bold,
                    fontSize: TYPOGRAPHY.fontSize['3xl'],
                    marginBottom: SPACING.lg,
                    lineHeight: TYPOGRAPHY.lineHeight.tight
                }}>Marine Fire & Smoke Detection</h1>
                <p style={{
                    fontSize: TYPOGRAPHY.fontSize.xl,
                    lineHeight: TYPOGRAPHY.lineHeight.relaxed,
                    marginBottom: 0
                }}>
                    Vital safety replacements and components for marine addressable and conventional fire alarm systems, keeping your vessel's detection network reliable and compliant.
                </p>
            </div>
        </section>

        {/* Category Detail Section */}
        <section className="py-5" style={{ background: COLORS.background.light }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-9">
                        <div className="category-detail-card fade-in-section">
                            <div className="category-icon-lg">
                                <i className="fa-solid fa-fire-extinguisher"></i>
                            </div>
                            <h2 style={{
                                color: COLORS.primary.dark,
                                fontWeight: TYPOGRAPHY.fontWeight.bold,
                                fontSize: TYPOGRAPHY.fontSize['2xl'],
                                marginBottom: SPACING.md
                            }}>
                                Fire & Smoke Detectors
                            </h2>
                            <p style={{
                                color: COLORS.text.primary,
                                fontSize: TYPOGRAPHY.fontSize.lg,
                                lineHeight: TYPOGRAPHY.lineHeight.relaxed
                            }}>
                                Vital safety replacements and components for marine addressable and conventional alarm systems.
                            </p>

                            <ul className="category-items">
                                {subItems.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                ))}
                            </ul>

                            <div className="brands-tag">
                                <strong>Supported Brands:</strong> Consilium, Salwico, Autronica, Apollo
                            </div>

                            <div className="compliance-note">
                                <strong>Compliance:</strong> All detectors, call points, and detection components we supply are sourced to meet SOLAS Chapter II-2 and FSS Code fire safety requirements, ensuring your vessel's fire and smoke detection system remains fully certified and survey-ready.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <div className="fade-in-section">
            <MarineSparesAdvantages />
        </div>

        {/* CTA Section */}
        <section className="cta-section py-5">
            <div className="container position-relative" style={{ zIndex: 1 }}>
                <div className="text-center" style={{ color: COLORS.text.white }}>
                    <h2 style={{
                        fontWeight: TYPOGRAPHY.fontWeight.bold,
                        fontSize: TYPOGRAPHY.fontSize['2xl'],
                        marginBottom: SPACING.md
                    }}>
                        Need Fire & Smoke Detection Spares? Get a Quote Today
                    </h2>
                    <p style={{
                        fontSize: TYPOGRAPHY.fontSize.lg,
                        marginBottom: SPACING.xl,
                        maxWidth: '600px',
                        margin: '0 auto',
                        opacity: 0.9,
                        lineHeight: TYPOGRAPHY.lineHeight.relaxed,
                        paddingBottom: SPACING.xl
                    }}>
                        Whether you need urgent detector replacements or sourcing of hard-to-find fire safety components, our expert team is ready to assist. Contact us for fast, reliable service.
                    </p>
                    <div className="cta-buttons d-flex justify-content-center gap-3 flex-wrap">
                        <a href="#contact" className="cta-btn cta-btn-primary">
                            <i className="fa-solid fa-file-invoice"></i>
                            Request a Quote
                        </a>
                        <a
                            href={`${SITE.whatsapp}?text=${whatsappMessage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cta-btn cta-btn-whatsapp"
                        >
                            <i className="fa-brands fa-whatsapp"></i>
                            WhatsApp Us
                        </a>
                    </div>
                </div>
            </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section py-5" style={{ background: COLORS.background.white }}>
            <div className="container">
                <h2>Frequently Asked Questions</h2>
                <p className="section-subtitle">
                    Find answers to common questions about our marine fire & smoke detection spare parts.
                </p>
                <div className="accordion" id="fireSmokeFAQ">
                    {faqs.map((faq, idx) => (
                        <div
                            className={`faq-card ${openIndex === idx ? 'open' : ''}`}
                            key={idx}
                        >
                            <div
                                className="faq-header"
                                onClick={() => toggleFAQ(idx)}
                            >
                                <h5>
                                    {faq.question}
                                    <span>{openIndex === idx ? "−" : "+"}</span>
                                </h5>
                            </div>
                            {openIndex === idx && (
                                <div className="faq-body">
                                    {faq.answer}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <div className="fade-in-section">
            <Contact />
        </div>

    </div>
    );
};

export default FireSmoke;
